import { US_MARKET_HOLIDAYS } from './us-market-holidays'

/** Project-wide default exchange timezone (NYSE/NASDAQ wall-clock). Used as the
 *  fallback wherever an exchange tz isn't known yet — matches the chart system's
 *  own `DEFAULT_SETTINGS.timezone` and the backend's `session_tz` default. */
export const DEFAULT_EXCHANGE_TZ = 'America/New_York'

/**
 * Extract date/time parts in a specific timezone using Intl.DateTimeFormat.
 * Returns { year, month (1-based), day, hour, minute, second, dayOfWeek (0=Sun) }.
 *
 * Exported so callers in the backtest chart (and anywhere else that needs to
 * gate "is this bar in the exchange's session window?") can resolve local
 * components from a UTC epoch without re-deriving the Intl boilerplate.
 */
export function getPartsInTimezone(
  date: Date,
  timeZone: string,
): {
  year: number
  month: number
  day: number
  hour: number
  minute: number
  second: number
  dayOfWeek: number
} {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    weekday: 'short',
    hour12: false,
  }).formatToParts(date)

  const get = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((p) => p.type === type)?.value ?? 0)

  const weekdayStr = parts.find((p) => p.type === 'weekday')?.value ?? ''
  const dayMap: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 }

  return {
    year: get('year'),
    month: get('month'),
    day: get('day'),
    hour: get('hour'),
    minute: get('minute'),
    second: get('second'),
    dayOfWeek: dayMap[weekdayStr] ?? 0,
  }
}

/**
 * Returns the current date/time in exchange timezone as a Date object.
 * The returned Date's calendar fields (getFullYear, getMonth, etc.) reflect exchange time.
 */
export function getExchangeNow(exchange: string = DEFAULT_EXCHANGE_TZ): Date {
  const p = getPartsInTimezone(new Date(), exchange)
  return new Date(p.year, p.month - 1, p.day, p.hour, p.minute, p.second)
}

/**
 * Returns today's date in exchange timezone as a YYYY-MM-DD string.
 */
export function getExchangeToday(exchange: string = DEFAULT_EXCHANGE_TZ): string {
  const p = getPartsInTimezone(new Date(), exchange)
  return `${p.year}-${String(p.month).padStart(2, '0')}-${String(p.day).padStart(2, '0')}`
}

/**
 * Normalize a YYYY-MM-DD focus date for "is this actually history": TODAY's
 * exchange date means LIVE and maps to null. The calendar's "Today" button
 * writes an explicit date (so an active workspace broadcast can't shadow the
 * click) — every history-mode consumer (chart anchor, yellow border, reset
 * chip) must treat that explicit today as live via this helper, or clicking
 * Today strands the chart in anchored mode.
 */
export function historicalFocusDate(dateStr: string | null | undefined): string | null {
  return dateStr && dateStr !== getExchangeToday() ? dateStr : null
}

/** Check if a YYYY-MM-DD date string is a US market holiday. */
export function isMarketHoliday(dateStr: string): boolean {
  return US_MARKET_HOLIDAYS.has(dateStr)
}

/** Check if a YYYY-MM-DD date string is a trading day (weekday + not a holiday). */
export function isTradingDay(dateStr: string): boolean {
  const d = new Date(dateStr + 'T12:00:00')
  const day = d.getDay()
  if (day === 0 || day === 6) return false
  return !US_MARKET_HOLIDAYS.has(dateStr)
}

/** Check if a Date object falls on a trading day. */
export function isTradingDayDate(date: Date): boolean {
  const day = date.getDay()
  if (day === 0 || day === 6) return false
  const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
  return !US_MARKET_HOLIDAYS.has(dateStr)
}

/**
 * Walk backwards from a date to find the last trading day (skips weekends + holidays).
 */
export function getLastTradingDayFromStr(dateStr: string): string {
  const d = new Date(dateStr + 'T12:00:00')
  while (!isTradingDayDate(d)) {
    d.setDate(d.getDate() - 1)
  }
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

/**
 * Returns the effective trading day — the trading session whose data is currently active.
 * Before 4 AM ET on weekdays, returns the previous trading day.
 * On weekends/holidays, returns the most recent trading day.
 */
export function getEffectiveTradingDay(exchange: string = DEFAULT_EXCHANGE_TZ): string {
  const p = getPartsInTimezone(new Date(), exchange)
  const todayStr = `${p.year}-${String(p.month).padStart(2, '0')}-${String(p.day).padStart(2, '0')}`

  // Weekend or holiday → walk back
  if (p.dayOfWeek === 0 || p.dayOfWeek === 6 || US_MARKET_HOLIDAYS.has(todayStr)) {
    return getLastTradingDayFromStr(todayStr)
  }

  // Before 4 AM ET → previous session
  if (p.hour * 60 + p.minute < 240) {
    const yesterday = new Date(p.year, p.month - 1, p.day)
    yesterday.setDate(yesterday.getDate() - 1)
    const yStr = `${yesterday.getFullYear()}-${String(yesterday.getMonth() + 1).padStart(2, '0')}-${String(yesterday.getDate()).padStart(2, '0')}`
    return getLastTradingDayFromStr(yStr)
  }

  return todayStr
}

/**
 * Returns the Unix timestamp (ms) of the next occurrence of the exchange-time
 * wall clock at HH:MM. If the current exchange time is already past HH:MM, the
 * timestamp lands on the same wall clock the following calendar day in the
 * exchange timezone. Handles DST transitions (see `wallTimeToUtcMs`).
 */
function getNextExchangeWallTime(hour: number, minute: number, exchange: string): number {
  const now = new Date()
  const p = getPartsInTimezone(now, exchange)

  let targetYear = p.year
  let targetMonth = p.month
  let targetDay = p.day
  const nowMinutes = p.hour * 60 + p.minute
  if (nowMinutes >= hour * 60 + minute) {
    const tomorrow = new Date(Date.UTC(p.year, p.month - 1, p.day))
    tomorrow.setUTCDate(tomorrow.getUTCDate() + 1)
    targetYear = tomorrow.getUTCFullYear()
    targetMonth = tomorrow.getUTCMonth() + 1
    targetDay = tomorrow.getUTCDate()
  }

  return wallTimeToUtcMs(targetYear, targetMonth, targetDay, hour, minute, exchange)
}

/** The zone's UTC offset at `instant`, in ms (wall clock minus UTC; negative in New York). */
function zoneOffsetMs(instant: number, timeZone: string): number {
  const p = getPartsInTimezone(new Date(instant), timeZone)
  return Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second) - instant
}

/**
 * A wall-clock time in `timeZone` as UTC epoch ms. Treat the wall clock as if it
 * were UTC, then shift by the zone's offset. The offset is read twice: first at
 * that naive instant, which sits hours from the real one, then at the first
 * answer. A clock change between the two (04:00 on a DST morning) would
 * otherwise land an hour off.
 */
function wallTimeToUtcMs(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  timeZone: string,
): number {
  const naive = Date.UTC(year, month - 1, day, hour, minute, 0)
  const firstGuess = naive - zoneOffsetMs(naive, timeZone)
  return naive - zoneOffsetMs(firstGuess, timeZone)
}

/**
 * Returns the Unix timestamp (ms) of the next 04:00 in exchange time — the
 * flush boundary for temporary ticker exclusions. 04:00 ET is the canonical
 * session boundary used by `getEffectiveTradingDay` (pre-market open). The
 * exclusion lasts through the rest of the current session (regular hours +
 * after-hours) and clears right as the next session begins.
 */
export function getNextExclusionFlushET(exchange: string = DEFAULT_EXCHANGE_TZ): number {
  return getNextExchangeWallTime(4, 0, exchange)
}

const YMD_RE = /^(\d{4})-(\d{2})-(\d{2})$/

/**
 * Resolve a `YYYY-MM-DD` calendar date + a wall-clock HH:MM in the exchange
 * timezone to the corresponding UTC epoch ms. DST-safe through `wallTimeToUtcMs`,
 * as `getNextExchangeWallTime` is. Returns `null` for a malformed / invalid date.
 *
 * Bar replay uses this to anchor a start pick at that day's regular-session
 * open — `exchangeWallTimeToUtcMs(dateStr, 9, 30)` — without hand-rolling any
 * timezone math at the call site.
 */
export function exchangeWallTimeToUtcMs(
  dateStr: string,
  hour: number,
  minute: number,
  exchange: string = DEFAULT_EXCHANGE_TZ,
): number | null {
  const m = YMD_RE.exec(dateStr)
  if (!m) return null
  const year = Number(m[1])
  const month = Number(m[2])
  const day = Number(m[3])
  // Round-trip validation catches 2024-02-30 etc.
  const probe = new Date(Date.UTC(year, month - 1, day))
  if (
    probe.getUTCFullYear() !== year ||
    probe.getUTCMonth() !== month - 1 ||
    probe.getUTCDate() !== day
  ) {
    return null
  }
  return wallTimeToUtcMs(year, month, day, hour, minute, exchange)
}
